import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import api from '../../../services/api';
import { getSettings } from '../../../models/settingsModel';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { API_BASE_URL } from '../../../config';
import html2pdf from 'html2pdf.js';
import Swal from 'sweetalert2';

const SupplierQuotation = () => {
    const { encryptedRef } = useParams();
    const [searchParams] = useSearchParams();
    const sid = searchParams.get('SID');
    const supplierName = searchParams.get('S');

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null); 
    
    const [rfqData, setRfqData] = useState(null);
    const [quoteDetails, setQuoteDetails] = useState(null);
    const [formData, setFormData] = useState({
        quotation_company_name: '',
        quotation_tel: '',
        quotation_physical_address: '',
        quotation_email_address: '',
        quotation_amounts: [],
        quotation_units: [],
        quotation_comments: [],
        quotation_vats: '',
        quotation_vat: '',
        quotation_total: '',
        logo: null
    });
    
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [encryptedParams, setEncryptedParams] = useState({});
    const [csrfToken, setCsrfToken] = useState('');
    const [vatAmounts, setVatAmounts] = useState([]);
    const [logoPreview, setLogoPreview] = useState('');
    const [settings, setSettings] = useState(null);
    const [vatCheckedStates, setVatCheckedStates] = useState([]);
    const [commentVisibility, setCommentVisibility] = useState([]);
    const [supplierCode, setSupplierCode] = useState(null); // Actual decoded supplier code
    const [uploadedAttachments, setUploadedAttachments] = useState([]); // Uploaded quotation files
    const [pdfDownloadData, setPdfDownloadData] = useState(null); // {pdf: base64, filename: string} for download
    const [specificationAttachments, setSpecificationAttachments] = useState([]); // Specification attachments from RFQ
    
    // Ref for specification section for PDF generation
    const specificationRef = useRef(null);

    // DISABLED: Track settings state changes (for debugging only)
    /*
    useEffect(() => {
        console.log('⚙️ Settings state changed:', settings);
        console.log('⚙️ Settings type:', typeof settings);
        console.log('⚙️ Settings keys:', settings ? Object.keys(settings) : 'null/undefined');
        if (settings) {
            console.log('⚙️ Settings content sample:', {
                site_name: settings.site_name,
                site_email: settings.site_email,
                total_keys: Object.keys(settings).length
            });
        }
    }, [settings]);
    */

    // DISABLED: Debug effect for VAT changes (causing re-renders)
    /*
    useEffect(() => {
        console.log('📊 VAT Amounts Changed:', {
            vatAmounts,
            formDataVats: formData.quotation_vats,
            quotationVat: formData.quotation_vat,
            quotationTotal: formData.quotation_total
        });
    }, [vatAmounts, formData.quotation_vats, formData.quotation_vat, formData.quotation_total]);
    */

    // Safe JSON parsing utility
    // Safe JSON parsing utility with PHP unserialize support
    const safeUnserialize = (serializedString, defaultValue = []) => {
        if (!serializedString || typeof serializedString !== 'string') {
            return Array.isArray(defaultValue) ? defaultValue : serializedString || defaultValue;
        }
        
        // Check if it's PHP serialized format (starts with 'a:' for array)
        if (serializedString.startsWith('a:')) {
            try {
                // Parse PHP serialized array format
                const result = parsePhpSerialized(serializedString);
                if (Array.isArray(result)) {
                    return result;
                }
            } catch (error) {
                // Error parsing PHP serialized string
            }
        }
        
        try {
            return JSON.parse(serializedString);
        } catch (error) {
            // JSON parse error
            return defaultValue;
        }
    };

    // PHP serialized array parser
    const parsePhpSerialized = (str) => {
        if (!str || typeof str !== 'string') return [];
        
        // Parse PHP serialized array format: a:2:{i:0;s:73:"...";i:1;s:106:"...";}
        try {
            const result = [];
            
            // Extract the content between the curly braces
            const match = str.match(/^a:\d+:\{(.+)\}$/s);
            if (!match) return [];
            
            const content = match[1];
            let pos = 0;
            
            while (pos < content.length) {
                // Skip whitespace
                while (pos < content.length && /\s/.test(content[pos])) pos++;
                
                // Look for index marker: i:N;
                const indexMatch = content.substring(pos).match(/^i:\d+;/);
                if (!indexMatch) break;
                pos += indexMatch[0].length;
                
                // Skip whitespace
                while (pos < content.length && /\s/.test(content[pos])) pos++;
                
                // Look for value type
                if (content.substring(pos).startsWith('s:')) {
                    // String value: s:BYTE_LENGTH:"VALUE";
                    // PHP serialize() stores byte length (UTF-8), but JS substring() counts
                    // characters (UTF-16). Multi-byte chars (e.g. ™ = 3 bytes, 1 char) make
                    // the byte count larger than the JS character count, causing overshoot.
                    // Fix: advance character-by-character until we have consumed exactly
                    // byteLength UTF-8 bytes, then verify the closing delimiter.
                    const lengthMatch = content.substring(pos).match(/^s:(\d+):/);
                    if (!lengthMatch) break;
                    
                    const byteLength = parseInt(lengthMatch[1], 10);
                    pos += lengthMatch[0].length; // Move past "s:BYTE_LENGTH:"
                    
                    // Expect opening quote
                    if (content[pos] !== '"') break;
                    pos++; // Skip opening quote
                    
                    // Consume exactly byteLength UTF-8 bytes by walking characters
                    let bytesConsumed = 0;
                    let strStart = pos;
                    while (pos < content.length && bytesConsumed < byteLength) {
                        const code = content.codePointAt(pos);
                        // Determine UTF-8 byte width of this code point
                        if (code < 0x80) {
                            bytesConsumed += 1;
                            pos += 1;
                        } else if (code < 0x800) {
                            bytesConsumed += 2;
                            pos += 1;
                        } else if (code < 0x10000) {
                            bytesConsumed += 3;
                            pos += 1;
                        } else {
                            // Surrogate pair (code > 0xFFFF): 4 UTF-8 bytes, 2 JS chars
                            bytesConsumed += 4;
                            pos += 2;
                        }
                    }
                    const stringValue = content.substring(strStart, pos);
                    
                    // Expect closing quote
                    if (content[pos] !== '"') break;
                    pos++; // Skip closing quote
                    
                    // Expect semicolon
                    if (content[pos] !== ';') break;
                    pos++; // Skip semicolon
                    
                    result.push(stringValue);
                    
                } else if (content.substring(pos).startsWith('i:')) {
                    // Integer value: i:VALUE;
                    const intMatch = content.substring(pos).match(/^i:(\d+);/);
                    if (!intMatch) break;
                    
                    result.push(parseInt(intMatch[1], 10));
                    pos += intMatch[0].length;
                }
            }
            
            return result;
        } catch (error) {
            // Error in parsePhpSerialized
            return [];
        }
    };

    useEffect(() => {
        // Only attempt to load data if we have the encrypted reference
        if (encryptedRef) {
            // For development/testing, we can proceed even if SID and S are missing
            // In production, all three parameters should be required
            if (sid && supplierName) {
                loadRFQData();
            } else {
                // For testing purposes, try to load with just the encrypted ref
                loadRFQData();
            }
        } else {
            // Set a more helpful error message when parameters are missing
            setError('Invalid or missing supplier link parameters. Please ensure you\'re accessing this page from a valid supplier link.');
            setLoading(false);
        }
        
        loadSettings();
    }, [encryptedRef, sid, supplierName]);

    const loadSettings = async () => {
        try {
            const settingsResponse = await getSettings();
            
            if (settingsResponse && settingsResponse.status === 1 && settingsResponse.data) {
                setSettings(settingsResponse.data);
            } else {
                setSettings(null);
            }
        } catch (error) {
            // Error loading settings via settingsModel
            
            // For supplier pages, we'll handle auth errors gracefully
            if (error.message?.includes('authentication') || error.message?.includes('login') || error.response?.status === 401) {
                // Fallback to procurement settings for supplier pages
                try {
                    const response = await api.get('/api/procurement/settings');
                    if (response.data && response.data.status === 1) {
                        setSettings(response.data.data);
                    } else {
                        setSettings(null);
                    }
                } catch (fallbackError) {
                    // Failed to load settings
                    setSettings(null);
                }
            } else {
                setSettings(null);
            }
        }
    };

    const fetchSpecificationAttachments = async (specificationId) => {
        if (!specificationId) return;
        
        try {
            // Use the public API endpoint for specification attachments
            // This endpoint doesn't require authentication for suppliers viewing RFQs
            const response = await api.get(`/api/attachments/specifications/${specificationId}/list?type=specification`);
            
            // Handle nested response structure: response.data.data.attachments
            const attachments = response.data?.data?.attachments || response.data?.attachments || [];
            setSpecificationAttachments(attachments);
        } catch (error) {
            // Error fetching specification attachments - not critical, just log it
            console.error('Error fetching specification attachments:', error);
            setSpecificationAttachments([]);
        }
    };

    const loadRFQData = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!encryptedRef) {
                throw new Error('Missing encrypted reference parameter');
            }

            // Build the API URL - SID and S are optional for testing
            let apiUrl = `/api/procurement/supplier/rfq/${encryptedRef}`;
            if (sid && supplierName) {
                apiUrl += `?SID=${sid}&S=${supplierName}`;
            } else if (sid) {
                apiUrl += `?SID=${sid}`;
            } else if (supplierName) {
                apiUrl += `?S=${supplierName}`;
            }

            const response = await api.get(apiUrl);
            
            if (response.data.status === 1) {
                const data = response.data.data;
                setRfqData(data.rfq);
                setQuoteDetails(data.quote_details);
                setEncryptedParams(data.encrypted_params);
                setCsrfToken(data.csrf_token);
                
                // Store the actual decoded supplier code
                if (data.supplier_code) {
                    setSupplierCode(data.supplier_code);
                }
                
                // Set logo preview if exists - prioritize base64 data to avoid path issues
                if (data.quote_details.supplier_logo_base64) {
                    // Best: Use base64 data directly (no path issues)
                    setLogoPreview(data.quote_details.supplier_logo_base64);
                    // Logo loaded as base64 from tb_supplier_attachments
                } else if (data.quote_details.supplier_logo_url) {
                    // Fallback: Use URL path
                    const logoUrl = data.quote_details.supplier_logo_url.startsWith('http') 
                        ? data.quote_details.supplier_logo_url 
                        : `${window.location.origin}/portal${data.quote_details.supplier_logo_url}`;
                    setLogoPreview(logoUrl);
                    // Logo loaded from URL
                } else if (data.quote_details.Logo) {
                    // Legacy: Old logo location
                    const legacyLogoUrl = `${window.location.origin}/portal/assets/logo/${data.quote_details.Logo}`;
                    setLogoPreview(legacyLogoUrl);
                    // Logo loaded from legacy location
                } else {
                    // No logo available for this supplier
                }
                
                // Initialize form data following PHP logic exactly
                const amounts = safeUnserialize(data.quote_details.quotation_amounts, []);
                const units = safeUnserialize(data.quote_details.quotation_units, []);
                const comments = safeUnserialize(data.quote_details.quotation_comments, []);
                
                // Get the number of items to ensure arrays are properly sized
                let itemCount = 0;
                try {
                    if (typeof data.rfq.procurement_items === 'string') {
                        try {
                            const items = JSON.parse(data.rfq.procurement_items);
                            itemCount = Array.isArray(items) ? items.length : 0;
                        } catch {
                            const items = safeUnserialize(data.rfq.procurement_items, []);
                            itemCount = items.length;
                        }
                    } else if (Array.isArray(data.rfq.procurement_items)) {
                        itemCount = data.rfq.procurement_items.length;
                    }
                } catch (error) {
                    // Error determining item count
                }
                
                // Ensure arrays are properly sized for all items
                const ensureArraySize = (arr, size) => {
                    const result = [...(arr || [])];
                    while (result.length < size) {
                        result.push('');
                    }
                    return result;
                };
                
                setFormData(prev => ({
                    ...prev,
                    quotation_company_name: data.supplier_name || '',
                    quotation_tel: data.quote_details.quotation_tel || data.quote_details.Tel || '',
                    quotation_physical_address: data.quote_details.quotation_physical_address || data.quote_details.Address || '',
                    quotation_email_address: data.quote_details.quotation_email_address || data.quote_details.E_Mail || '',
                    quotation_amounts: ensureArraySize(amounts, itemCount),
                    quotation_units: ensureArraySize(units, itemCount),
                    quotation_comments: ensureArraySize(comments, itemCount),
                    quotation_vats: data.quote_details.quotation_vats || '',
                    quotation_vat: data.quote_details.quotation_vat || '',
                    quotation_total: data.quote_details.quotation_total || ''
                }));
                
                // Initialize VAT amounts - ensure proper size
                let initialVats = [];
                if (data.quote_details.quotation_vats) {
                    initialVats = data.quote_details.quotation_vats.split(',').map(v => parseFloat(v.trim()) || 0);
                }
                // Ensure VAT array is same size as items
                while (initialVats.length < itemCount) {
                    initialVats.push(0);
                }
                setVatAmounts(initialVats);
                
                // Initialize comment visibility - show input for existing comments
                const initialCommentVisibility = comments.map(comment => 
                    comment && comment.trim() !== '' // Show input if comment has content
                );
                // Ensure visibility array is same size as items
                while (initialCommentVisibility.length < itemCount) {
                    initialCommentVisibility.push(false);
                }
                setCommentVisibility(initialCommentVisibility);
                
                // Load uploaded attachments from tb_requisition_attachments
                if (data.quote_details.attachments && data.quote_details.attachments.length > 0) {
                    setUploadedAttachments(data.quote_details.attachments);
                    // Loaded uploaded attachments
                } else {
                    setUploadedAttachments([]);
                }
                
                // Load specification attachments if available
                // These come from tb_requisition_attachments with type 'specification'
                if (data.rfq.specification_id) {
                    fetchSpecificationAttachments(data.rfq.specification_id);
                } else {
                    setSpecificationAttachments([]);
                }
                
                // Initialize attached files (note: these are just for display, not for re-uploading)
                // The actual files are stored on the server, we just show their names
                if (data.quote_details.files && data.quote_details.files.length > 0) {
                    // Don't add to attachedFiles as these are already uploaded
                    // attachedFiles is only for new files being uploaded
                }
                
                setRfqData(data.rfq);
                setQuoteDetails(data.quote_details);
                setEncryptedParams({
                    qid: data.encrypted_params.qid,
                    rfq: data.encrypted_params.rfq,
                    pid: data.encrypted_params.pid,
                    code: data.encrypted_params.code
                });
                
            } else {
                throw new Error(response.data.message || 'Failed to load RFQ data');
            }
        } catch (error) {
            // Error loading RFQ data
            setError(error.message || 'Failed to load RFQ data');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = useCallback((field, value, index = null) => {
        setFormData(prev => {
            const newData = { ...prev };
            if (index !== null) {
                if (!Array.isArray(newData[field])) {
                    newData[field] = [];
                }
                // Ensure the array is large enough
                while (newData[field].length <= index) {
                    newData[field].push('');
                }
                newData[field][index] = value;
            } else {
                newData[field] = value;
            }
            return newData;
        });
    }, []); // Empty dependency array since setFormData is stable

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type and size
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            const maxSize = 5 * 1024 * 1024; // 5MB
            
            if (!allowedTypes.includes(file.type)) {
                setError('Please select a valid image file (JPG, PNG, GIF)');
                return;
            }
            
            if (file.size > maxSize) {
                setError('Logo file size must be less than 5MB');
                return;
            }
            
            // Create preview immediately
            const reader = new FileReader();
            reader.onload = (event) => {
                setLogoPreview(event.target.result);
            };
            reader.readAsDataURL(file);
            
            // Upload logo immediately to tb_supplier_attachments
            try {
                setSubmitting(true);
                const logoFormData = new FormData();
                logoFormData.append('file', file);
                // Use the actual decoded supplier code, not the encrypted one
                logoFormData.append('supplier_id', supplierCode || 'unknown');
                logoFormData.append('uploaded_by_name', formData.quotation_company_name || 'Supplier');
                logoFormData.append('document_type', 'logo');
                logoFormData.append('is_public', '1');
                
                const logoResponse = await api.post('/api/attachments/suppliers', logoFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                if (logoResponse.data.status === 1) {
                    setSuccess('Logo uploaded successfully');
                    // Store the attachment info (not needed in form submission)
                    setFormData(prev => ({ ...prev, logo: logoResponse.data.data.file_path }));
                } else {
                    setError('Failed to upload logo');
                    setLogoPreview(''); // Clear preview on error
                }
            } catch (logoError) {
                // Logo upload failed
                setError(logoError.response?.data?.message || 'Failed to upload logo');
                setLogoPreview(''); // Clear preview on error
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) {
            return;
        }

        if (!quoteDetails?.quotation_id) {
            setError('Cannot upload files: Quotation ID not available');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            
            const formData = new FormData();
            
            // Add all files to form data
            files.forEach((file, index) => {
                formData.append(`attachments[${index}]`, file);
            });
            
            // Add uploader name
            formData.append('uploaded_by_name', quoteDetails.quotation_company_name || 'Supplier');

            const response = await api.post(
                `/api/procurement/supplier/quotation/${quoteDetails.quotation_id}/attachments`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data.status === 1) {
                setSuccess(`${response.data.uploads} file(s) uploaded successfully`);
                
                // Clear the file input
                setAttachedFiles([]);
                
                // Reload RFQ data to show the newly uploaded files
                await loadRFQData();
                
                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.data.message || 'Failed to upload files');
            }
        } catch (error) {
            // File upload failed
            setError(error.response?.data?.message || 'Failed to upload files');
        } finally {
            setSubmitting(false);
        }
    };

    const handleFileDelete = async (attachmentId) => {
        if (!quoteDetails?.quotation_id) {
            setError('Cannot delete file: Quotation ID not available');
            return;
        }

        // Confirm deletion with SweetAlert2
        const result = await Swal.fire({
            title: 'Delete File?',
            text: 'Are you sure you want to delete this file? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            const response = await api.delete(
                `/api/procurement/supplier/quotation/${quoteDetails.quotation_id}/attachments/${attachmentId}`
            );

            if (response.data.status === 1) {
                setSuccess('File deleted successfully');
                
                // Reload RFQ data to update the file list
                await loadRFQData();
                
                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(response.data.message || 'Failed to delete file');
            }
        } catch (error) {
            // File delete failed
            setError(error.response?.data?.message || 'Failed to delete file');
        } finally {
            setSubmitting(false);
        }
    };

    // Simple calculation function matching AttachQuotationModal pattern
    const calculateTotals = (currentItems, currentVats) => {
        // Calculate total VAT
        const totalVat = Object.values(currentVats).reduce((sum, vat) => sum + (parseFloat(vat) || 0), 0);
        
        // Calculate grand total (items + VAT)
        const itemsTotal = currentItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        const total = itemsTotal + totalVat;
        
        // Convert currentVats to array format for submission
        // Ensure we have an array with values in the correct order
        const vatArray = [];
        currentItems.forEach((item, index) => {
            vatArray[index] = (currentVats[index] || 0).toString();
        });
        
        // Update form data with VAT details
        setFormData(prev => ({
            ...prev,
            quotation_vat: totalVat.toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
            }).replace(/,/g, ' '),
            quotation_total: total.toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
            }).replace(/,/g, ' '),
            quotation_vats: vatArray
        }));
    };

    const handleItemPriceChange = (index, value) => {
        // Remove currency formatting for calculation
        const cleanValue = value.replace(/[R\\s,]/g, '');
        const unitPrice = parseFloat(cleanValue) || 0;
        
        // Get quantity for this item
        let quantities = [];
        try {
            if (typeof rfqData.procurement_qty === 'string') {
                quantities = safeUnserialize(rfqData.procurement_qty, []);
            } else if (Array.isArray(rfqData.procurement_qty)) {
                quantities = rfqData.procurement_qty;
            }
        } catch (error) {
            // Error parsing quantities
            quantities = [];
        }
        
        const quantity = quantities[index] || 0;
        const total = unitPrice * quantity;
        
        // Update the unit price in formData
        setFormData(prev => {
            const newUnits = [...prev.quotation_units];
            newUnits[index] = cleanValue;
            
            const newAmounts = [...prev.quotation_amounts];
            newAmounts[index] = total.toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
            }).replace(/,/g, ' ');
            
            return {
                ...prev,
                quotation_units: newUnits,
                quotation_amounts: newAmounts
            };
        });
        
        // Update VAT amounts based on current VAT checkbox state
        const updatedVats = [...vatAmounts];
        while (updatedVats.length <= index) {
            updatedVats.push(0);
        }
        if (vatCheckedStates[index]) {
            updatedVats[index] = total * 0.15;
        } else {
            updatedVats[index] = 0;
        }
        
        setVatAmounts(updatedVats);
        
        // Calculate totals
        const currentItems = formData.quotation_amounts.map((amount, i) => ({
            total: i === index ? total : parseFloat(amount.replace(/[R\s,]/g, '')) || 0
        }));
        
        calculateTotals(currentItems, updatedVats);
    };

    const toggleVat = (index) => {
        // Update VAT checkbox state
        setVatCheckedStates(prev => {
            const newStates = [...prev];
            while (newStates.length <= index) {
                newStates.push(false);
            }
            newStates[index] = !newStates[index];
            return newStates;
        });
        
        // Update VAT amounts
        const updatedVats = [...vatAmounts];
        while (updatedVats.length <= index) {
            updatedVats.push(0);
        }
        const currentAmount = parseFloat(formData.quotation_amounts[index]?.replace(/[R\s,]/g, '') || 0);
        
        if (!vatCheckedStates[index]) {
            // VAT is being enabled
            updatedVats[index] = currentAmount * 0.15;
        } else {
            // VAT is being disabled
            updatedVats[index] = 0;
        }
        
        setVatAmounts(updatedVats);
        
        // Calculate totals
        const currentItems = formData.quotation_amounts.map((amount, i) => ({
            total: parseFloat(amount.replace(/[R\s,]/g, '')) || 0
        }));
        
        calculateTotals(currentItems, updatedVats);
    };

    // DISABLED: Effect to initialize calculations and VAT state when data is loaded (causing infinite re-renders)
    /*
    useEffect(() => {
        if (!rfqData || !quoteDetails || !formData.quotation_units) return;
        
        // console.log('🔄 Initializing calculations after data load...');
        
        // Initialize VAT checkboxes from existing data (like original JavaScript)
        const existingVats = formData.quotation_vats;
        if (existingVats && existingVats !== '') {
            const vatValues = existingVats.split(',').map(v => parseFloat(v.trim()) || 0);
            // console.log('📊 Loading existing VAT values:', vatValues);
            
            // Set checkbox states based on existing VAT values
            const checkedStates = vatValues.map(vat => vat > 0);
            setVatCheckedStates(checkedStates);
            // console.log('✅ Setting VAT checkbox states:', checkedStates);
            
            // Trigger calculation after state is set
            setTimeout(() => doCalculate(), 100);
        } else {
            // No existing VATs, just do initial calculation
            setTimeout(() => doCalculate(), 100);
        }
    }, [rfqData, quoteDetails, formData.quotation_units, formData.quotation_vats, doCalculate]);
    */

    // DISABLED: Effect to recalculate totals when unit prices or VAT amounts change
    /*
    React.useEffect(() => {
        if (!rfqData || !formData.quotation_units) return;

        const newAmounts = [];
        const newVatAmounts = [...vatAmounts];

        // Parse quantities once
        const qtys = (() => {
            if (!rfqData.procurement_qty) return [];
            try {
                if (typeof rfqData.procurement_qty === 'string') {
                    try {
                        const unserialized = safeUnserialize(rfqData.procurement_qty, []);
                        if (Array.isArray(unserialized) && unserialized.length > 0) {
                            return unserialized.map(v => parseFloat(v) || 0);
                        }
                    } catch {
                        try {
                            const jsonParsed = JSON.parse(rfqData.procurement_qty);
                            return Array.isArray(jsonParsed) ? jsonParsed.map(v => parseFloat(v) || 0) : [];
                        } catch {
                            const csvValues = rfqData.procurement_qty.split(',').map(v => v.trim());
                            return csvValues.map(v => parseFloat(v) || 0);
                        }
                    }
                }
                return Array.isArray(rfqData.procurement_qty) ? rfqData.procurement_qty.map(v => parseFloat(v) || 0) : [];
            } catch {
                return [];
            }
        })();

        // Calculate line totals for each item
        formData.quotation_units.forEach((unitPrice, index) => {
            const cleanValue = (unitPrice || '').toString().replace(/[R\s,]/g, '');
            const price = parseFloat(cleanValue) || 0;
            const qty = parseFloat(qtys[index]) || 0;
            const lineTotal = price * qty;

            // Format line total
            const formattedTotal = lineTotal.toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
            }).replace(/,/g, ' ');

            newAmounts[index] = formattedTotal;

            // Calculate VAT if checkbox is checked
            if (newVatAmounts[index] > 0 && lineTotal > 0) {
                newVatAmounts[index] = lineTotal * 0.15;
            } else if (lineTotal === 0) {
                newVatAmounts[index] = 0;
            }
        });

        // Update amounts
        setFormData(prev => ({
            ...prev,
            quotation_amounts: newAmounts
        }));

        // Update VAT amounts if they changed
        const vatsChanged = newVatAmounts.some((vat, index) => vat !== vatAmounts[index]);
        if (vatsChanged) {
            setVatAmounts(newVatAmounts);
        }

    }, [formData.quotation_units, rfqData]);
    */

    // ALSO DISABLED: Effect to update totals when VAT amounts change (causing re-renders)
    /*
    React.useEffect(() => {
        if (!formData.quotation_amounts || !vatAmounts) return;

        // Calculate total VAT
        const totalVat = vatAmounts.reduce((sum, vat) => sum + (parseFloat(vat) || 0), 0);

        // Calculate total amount (line totals + VAT)
        const lineTotalsSum = formData.quotation_amounts.reduce((sum, amount) => {
            const cleanAmount = (amount || '0').toString().replace(/[R\s,]/g, '');
            return sum + (parseFloat(cleanAmount) || 0);
        }, 0);

        const grandTotal = lineTotalsSum + totalVat;

        // Format numbers
        const formatNumber = (num) => {
            return num.toLocaleString('en-US', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2
            }).replace(/,/g, ' ');
        };

        // Update totals
        setFormData(prev => ({
            ...prev,
            quotation_vat: formatNumber(totalVat),
            quotation_total: formatNumber(grandTotal),
            quotation_vats: vatAmounts.join(',')
        }));

    }, [vatAmounts, formData.quotation_amounts]);
    */

    const toggleCommentBox = (index) => {
        // Toggle comment visibility for this specific item using separate visibility state
        setCommentVisibility(prev => {
            const newVisibility = [...prev];
            // Ensure array is large enough
            while (newVisibility.length <= index) {
                newVisibility.push(false);
            }
            // Toggle visibility
            newVisibility[index] = !newVisibility[index];
            return newVisibility;
        });
        
        // Also initialize the comment value if it doesn't exist
        setFormData(prev => {
            const newComments = [...prev.quotation_comments];
            // Ensure array is large enough
            while (newComments.length <= index) {
                newComments.push('');
            }
            // Initialize with empty string if not already set
            if (newComments[index] === undefined || newComments[index] === null) {
                newComments[index] = '';
            }
            return {
                ...prev,
                quotation_comments: newComments
            };
        });
    };

    const handleSubmit = async (e, action = 'save') => {
        // Prevent default form submission behavior
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        try {
            setSubmitting(true);
            setError(null);
            
            // console.log('=== SUBMISSION STARTED ===');
            // console.log('Action:', action);

            const submitData = new FormData();
            
            // Add form fields - use PHP array format for arrays that backend expects to iterate over
            Object.keys(formData).forEach(key => {
                // Skip logo here - we'll handle it separately as a file
                if (key === 'logo') {
                    return;
                } else if (Array.isArray(formData[key])) {
                    // Backend expects these specific arrays in PHP format for foreach() loops
                    if (key === 'quotation_amounts' || key === 'quotation_units' || key === 'quotation_comments' || key === 'quotation_vats') {
                        formData[key].forEach((value, index) => {
                            submitData.append(`${key}[${index}]`, value || '');
                        });
                    } else {
                        // Other arrays can use JSON format
                        submitData.append(key, JSON.stringify(formData[key]));
                    }
                } else {
                    submitData.append(key, formData[key] || '');
                }
            });

            // Logo is already uploaded, just include filename if available
            if (formData.logo && typeof formData.logo === 'string') {
                submitData.append('logo_filename', formData.logo);
            }

            // Add attached files with proper naming for backend
            if (attachedFiles.length > 0) {
                attachedFiles.forEach((file, index) => {
                    submitData.append(`files[${index}]`, file);
                });
            }

            // Add encrypted parameters
            Object.keys(encryptedParams).forEach(key => {
                submitData.append(key, encryptedParams[key]);
            });

            // Add CSRF token
            submitData.append('csrf_token', csrfToken);

            // Add action
            if (action === 'complete') {
                submitData.append('complete', '1');
            } else {
                submitData.append('save', '1');
            }



            

            

            

            

            

            

            

            
            // Proceeding with submission

            const response = await api.post('/api/procurement/supplier/submit', submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Handle various response formats and success conditions
            let isSuccess = false;
            let responseMessage = '';

            // Check for success in multiple formats
            if (response.data) {
                // Format 1: { status: 1, message: "..." }
                if (response.data.status === 1 || response.data.status === '1') {
                    isSuccess = true;
                    responseMessage = response.data.message;
                }
                // Format 2: { success: true, message: "..." }
                else if (response.data.success === true || response.data.success === 'true') {
                    isSuccess = true;
                    responseMessage = response.data.message;
                }
                // Format 3: HTTP 200 with any data (fallback)
                else if (response.status === 200 && !response.data.error) {
                    isSuccess = true;
                    responseMessage = response.data.message || 'Quotation submitted successfully';
                }
            }

            if (isSuccess) {
                setSuccess(responseMessage || 'Quotation submitted successfully');
                
                if (action === 'complete') {
                    // Reload to show confirmation form
                    setTimeout(() => loadRFQData(), 1000);
                }
            } else {
                // Get error message from various possible locations
                const errorMessage = response.data?.message || 
                                   response.data?.error || 
                                   response.data?.msg || 
                                   'Submission failed - please try again';
                
                // Server returned non-success status
                
                throw new Error(errorMessage);
            }
        } catch (error) {
            // Error submitting quotation
            
            // If it's a network error or server error, suggest checking back
            if (error.response?.status >= 500 || !error.response) {
                setError('Submission may have been processed. Please refresh the page to check status. If issue persists: ' + (error.message || 'Network error'));
            } else {
                setError(error.response?.data?.message || error.message || 'Failed to submit quotation');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmSubmission = async (otpCode = null) => {
        try {
            // If no OTP code provided, show SweetAlert2 input modal
            if (!otpCode) {
                const result = await Swal.fire({
                    title: 'Confirm Submission',
                    html: `
                        <p class="text-sm text-gray-600 mb-4">
                            Please enter the confirmation code sent to your email
                        </p>
                    `,
                    input: 'text',
                    inputPlaceholder: 'Enter confirmation code',
                    showCancelButton: true,
                    confirmButtonColor: '#2563eb',
                    cancelButtonColor: '#6b7280',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                    showDenyButton: true,
                    denyButtonText: 'Resend Code',
                    denyButtonColor: '#059669',
                    inputValidator: (value) => {
                        if (!value || !value.trim()) {
                            return 'Please enter the confirmation code';
                        }
                    }
                });

                if (result.isDenied) {
                    // User clicked "Resend Code"
                    await handleResendCode();
                    // Show the modal again after resending
                    return handleConfirmSubmission();
                }

                if (!result.isConfirmed) {
                    // User cancelled
                    return;
                }

                otpCode = result.value;
            }

            setSubmitting(true);
            setError(null);

            const confirmData = {
                otp: otpCode,
                rfq: encryptedParams.rfq || encryptedParams.ref,
                sid: sid, // Use the sid from URL parameters
                pid: rfqData?.procurement_id, // Use the procurement ID from RFQ data
                eid: formData.quotation_email_address || quoteDetails?.E_Mail, // Use email from form or quote details
                qid: quoteDetails?.quotation_id // Add quotation ID for reference
            };

            const response = await api.post('/api/procurement/supplier/confirm', confirmData);

            if (response.data.status === 1) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Quotation confirmed successfully!',
                    confirmButtonColor: '#2563eb'
                });
                
                // Store download params for PDF download (encrypted, no session needed)
                // Check both possible locations
                const downloadParams = response.data.data?.download_params || response.data.download_params;
                const filename = response.data.data?.filename || response.data.filename;
                
                if (downloadParams && typeof downloadParams === 'object' && downloadParams.sid) {
                    // Setting download params
                    setPdfDownloadData({
                        params: downloadParams,
                        filename: filename
                    });
                    // Download params set successfully
                } else {
                    // Download params missing or invalid in response
                }
                
                // Reload data to show updated status
                loadRFQData();
            } else {
                throw new Error(response.data.message || 'Confirmation failed');
            }
        } catch (error) {
            // Error confirming submission
            await Swal.fire({
                icon: 'error',
                title: 'Confirmation Failed',
                text: error.response?.data?.message || error.message || 'Failed to confirm submission',
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleResendCode = async () => {
        try {
            // sid is already the encrypted SID from URL query parameter
            // encryptedParams.rfq is the encrypted RFQ reference
            const response = await api.post(`/api/procurement/supplier/resend/${sid}?ref=${encryptedParams.rfq}`);
            
            if (response.data.status === 1) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Code Sent!',
                    text: 'Confirmation code has been resent to your email',
                    confirmButtonColor: '#2563eb',
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                throw new Error(response.data.message || 'Failed to resend code');
            }
        } catch (error) {
            // Error resending code
            await Swal.fire({
                icon: 'error',
                title: 'Resend Failed',
                text: error.response?.data?.message || error.message || 'Failed to resend confirmation code',
                confirmButtonColor: '#dc2626'
            });
        }
    };

    // Handle PDF download using encrypted params (session-less)
    const handleDownloadPDF = () => {
        if (!pdfDownloadData || !pdfDownloadData.params) {
            setError('Download parameters not available');
            return;
        }

        try {
            const params = pdfDownloadData.params;
            // Build URL with individual encrypted parameters
            const downloadUrl = `${API_BASE_URL}/api/procurement/supplier/quote/download?sid=${encodeURIComponent(params.sid)}&rfq=${encodeURIComponent(params.rfq)}&pid=${encodeURIComponent(params.pid)}&qid=${encodeURIComponent(params.qid)}`;
            window.open(downloadUrl, '_blank');
        } catch (error) {
            // Error downloading PDF
            setError('Failed to download PDF. Please try again.');
        }
    };

    // Download specification as PDF from HTML
    const handleDownloadSpecification = () => {
        if (!specificationRef.current) {
            setError('Specification content not available');
            return;
        }

        try {
            const element = specificationRef.current;
            const filename = `Specification_${rfqData?.procurement_ref || 'RFQ'}.pdf`;
            
            // Show PDF header temporarily for PDF generation
            const pdfHeader = element.querySelector('.pdf-header');
            if (pdfHeader) {
                pdfHeader.style.display = 'block';
            }
            
            const opt = {
                margin: [10, 10, 10, 10],
                filename: filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { 
                    scale: 2, 
                    useCORS: true,
                    logging: false,
                    windowWidth: element.scrollWidth,
                    windowHeight: element.scrollHeight
                },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            };

            html2pdf().set(opt).from(element).save().then(() => {
                // Hide PDF header again after generation
                if (pdfHeader) {
                    pdfHeader.style.display = 'none';
                }
            }).catch((error) => {
                // PDF generation error
                if (pdfHeader) {
                    pdfHeader.style.display = 'none';
                }
                setError('Failed to generate specification PDF. Please try again.');
            });
        } catch (error) {
            // Error generating specification PDF
            setError('Failed to generate specification PDF. Please try again.');
        }
    };

    // Check if quotation is already submitted and pending confirmation
    const isPendingConfirmation = () => {
        // PHP logic: quotation_status == 2
        return quoteDetails?.quotation_status == 2 || quoteDetails?.quotation_status === '2';
    };

    // Check if quotation is completed
    const isCompleted = () => {
        // PHP logic: quotation_status == 3
        return quoteDetails?.quotation_status == 3 || quoteDetails?.quotation_status === '3';
    };

    // Check if RFQ is unpublished
    const isRFQUnpublished = () => {
        if (!rfqData) return false;
        return rfqData.procurement_status == 0 || rfqData.procurement_status === '0';
    };

    // Check if RFQ is still open for submissions
    const isRFQOpen = () => {
        if (!rfqData) return false;
        
        // Check if RFQ is unpublished (procurement_status == 0)
        if (isRFQUnpublished()) {
            return false;
        }
        
        // PHP logic: procurement_closed == 0 && time() < strtotime($date . ' ' . $procurement_closing_time)
        // First check if procurement is manually closed
        if (rfqData.procurement_closed == 1 || rfqData.procurement_closed === '1') {
            return false;
        }
        
        try {
            // Handle the date format from PHP (DD/MM/YYYY)
            let submissionDate;
            
            if (rfqData.procurement_closing && rfqData.procurement_closing_time) {
                // Convert DD/MM/YYYY to YYYY-MM-DD format for proper parsing
                const dateParts = rfqData.procurement_closing.split('/');
                if (dateParts.length === 3) {
                    // Convert from DD/MM/YYYY to YYYY-MM-DD
                    const formattedDate = `${dateParts[2]}-${dateParts[1].padStart(2, '0')}-${dateParts[0].padStart(2, '0')}`;
                    submissionDate = new Date(`${formattedDate} ${rfqData.procurement_closing_time}`);
                } else {
                    // Fallback to direct parsing
                    submissionDate = new Date(`${rfqData.procurement_closing} ${rfqData.procurement_closing_time}`);
                }
            } else if (rfqData.procurement_submission_date) {
                submissionDate = new Date(rfqData.procurement_submission_date);
            } else {
                // No submission date found in RFQ data
                return true; // Default to open if we can't determine the date
            }
            
            const now = new Date();
            const isOpen = now <= submissionDate;
            
            return isOpen;
        } catch (error) {
            // Error parsing submission deadline
            
            // If we can't parse the date, default to open (safer for suppliers)
            return true;
        }
    };

    // Get quantities for use in the table
    const quantities = (() => {
        if (!rfqData || !rfqData.procurement_qty) return [];
        try {
            // Handle different data formats - could be JSON string, serialized, or array
            if (typeof rfqData.procurement_qty === 'string') {
                // Try to unserialize PHP format first
                try {
                    const unserialized = safeUnserialize(rfqData.procurement_qty, []);
                    if (Array.isArray(unserialized) && unserialized.length > 0) {
                        return unserialized.map(v => parseFloat(v) || 0);
                    }
                } catch (unserializeError) {
                    // Failed to unserialize, try JSON
                }
                
                // Try JSON format
                try {
                    const jsonParsed = JSON.parse(rfqData.procurement_qty);
                    return Array.isArray(jsonParsed) ? jsonParsed.map(v => parseFloat(v) || 0) : [];
                } catch (jsonError) {
                    // If JSON fails, try as comma-separated values
                    const csvValues = rfqData.procurement_qty.split(',').map(v => v.trim());
                    return csvValues.map(v => parseFloat(v) || 0);
                }
            }
            return Array.isArray(rfqData.procurement_qty) ? rfqData.procurement_qty.map(v => parseFloat(v) || 0) : [];
        } catch (error) {
            // Error parsing procurement_qty
            return [];
        }
    })();

    // Completed View Component
    const CompletedView = () => (
        <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="bg-green-600 px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-white">
                    Quotation Successfully Submitted
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-green-100">
                    Your quotation has been received and is being reviewed
                </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
                <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Thank you for your submission</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        We will review your quotation and get back to you soon.
                    </p>
                    
                    {/* Download Copy button - matches original PHP behavior when $_SESSION['Quote'] is set */}
                    {quoteDetails?.quotation_id && (
                        <div className="mt-6">
                            <button
                                onClick={() => window.open(`/api/procurement/download/${encryptedParams.rfq}?type=quote&id=${quoteDetails.quotation_id}`, '_blank')}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                                style={{ 
                                    background: 'var(--gradient-card-success)',
                                    focusRingColor: 'var(--color-success)'
                                }}
                            >
                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Copy
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // Closed View Component  
    const ClosedView = () => (
        <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="bg-red-600 px-4 py-3 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-white">
                    RFQ Closed
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-red-100">
                    The submission deadline for this RFQ has passed
                </p>
            </div>
            <div className="px-4 py-5 sm:p-6">
                <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Submission Period Ended</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        This RFQ is no longer accepting submissions.
                    </p>
                </div>
            </div>
        </div>
    );

    // Main Quotation Form Component - Compact version matching PHP structure
    const QuotationForm = () => {
        // DISABLED: Debug logging for form state (causing infinite re-renders)
        /*
        React.useEffect(() => {
            console.log('📝 QuotationForm Debug:', {
                formData,
                vatAmounts,
                quantities,
                itemCount: quantities.length,
                formArraySizes: {
                    amounts: formData.quotation_amounts?.length,
                    units: formData.quotation_units?.length,
                    comments: formData.quotation_comments?.length
                }
            });
        }, [formData, vatAmounts, quantities]);
        */
        
        return (
        <div className="bg-white shadow rounded-lg overflow-hidden">
            <form onSubmit={(e) => handleSubmit(e, 'save')}>
                {/* Supplier Details - Compact Table Format */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Supplier Details</h4>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-300">
                            <tbody className="bg-white">
                                <tr className="border-b border-gray-200">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50 w-1/4">Company Name</td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="text"
                                            value={formData.quotation_company_name}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                                            readOnly
                                        />
                                    </td>
                                </tr>
                                <tr className="border-b border-gray-200">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">
                                        Logo <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Optional</span>
                                        {logoPreview && (
                                            <img src={logoPreview} alt="Logo preview" className="mt-2 max-h-16 max-w-32 object-contain" />
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoChange}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                    </td>
                                </tr>
                                <tr className="border-b border-gray-200">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">Contact Number</td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="text"
                                            value={formData.quotation_tel}
                                            onChange={(e) => handleInputChange('quotation_tel', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </td>
                                </tr>
                                <tr className="border-b border-gray-200">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">Physical Address</td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="text"
                                            value={formData.quotation_physical_address}
                                            onChange={(e) => handleInputChange('quotation_physical_address', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </td>
                                </tr>
                                <tr className="border-b border-gray-200">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">Email Address</td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="text"
                                            value={formData.quotation_email_address}
                                            onChange={(e) => handleInputChange('quotation_email_address', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="email@example.com, email2@example.com"
                                        />
                                        <span className="mt-1 text-xs text-blue-600">You may enter up to 3 comma separated email addresses</span>
                                    </td>
                                </tr>
                                {rfqData?.procurement_attach == 1 && (
                                    <tr>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">
                                            Files
                                            <div className="mt-1 space-x-2">
                                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                                    Allowed: {settings?.site_allowed_ext || 'PDF, DOC, DOCX'}
                                                </span>
                                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                                    Max: {settings?.site_upload_limit || '5'}MB
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                onClick={() => setShowFileUpload(!showFileUpload)}
                                                className="mb-4 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                            >
                                                <svg className="inline w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                </svg>
                                                Attach Files
                                            </button>
                                            
                                            {/* MultiFileInput Component - uploads immediately on selection */}
                                            {showFileUpload && (
                                                <div className="mb-4">
                                                    <MultiFileInput
                                                        files={attachedFiles}
                                                        onChange={(newFiles) => {
                                                            setAttachedFiles(newFiles);
                                                            // Upload immediately when files are selected
                                                            if (newFiles.length > 0) {
                                                                handleFileUpload(newFiles);
                                                            }
                                                        }}
                                                        multiple={true}
                                                        className="w-full"
                                                    />
                                                </div>
                                            )}
                                            
                                            {/* Show uploaded attachments from tb_requisition_attachments */}
                                            {uploadedAttachments.length > 0 && (
                                                <div className="mt-4">
                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h5>
                                                    <ul className="space-y-2">
                                                        {uploadedAttachments.map((attachment) => (
                                                            <li key={attachment.attachment_id} className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded flex items-center justify-between group hover:bg-gray-100 transition-colors">
                                                                <div className="flex items-center flex-1 min-w-0">
                                                                    <svg className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    <span className="truncate" title={attachment.file_name}>
                                                                        {attachment.file_name}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleFileDelete(attachment.attachment_id)}
                                                                    disabled={submitting}
                                                                    className="ml-2 flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    title="Delete file"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            
                                            {/* Show legacy files from database (fallback) */}
                                            {quoteDetails?.files && quoteDetails.files.length > 0 && (
                                                <div className="mt-4">
                                                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                                                        Previously Uploaded Files (legacy):
                                                    </h5>
                                                    <ul className="space-y-1">
                                                        {quoteDetails.files.map((file, index) => (
                                                            <li key={index} className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded flex items-center justify-between">
                                                                <span className="flex-1 truncate" title={file.quotation_file_name}>
                                                                    {file.quotation_file_name}
                                                                </span>
                                                                <span className="ml-2 text-xs text-gray-500 flex-shrink-0">(legacy)</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Quotation Items - Compact Table */}
                <div className="px-6 py-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Quotation</h4>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-300">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-2/5">Item</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-1/12">Qty</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-1/5">Unit Price</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-1/12">VAT</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Total</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {(() => {
                                    try {
                                        let items = [];
                                        if (typeof rfqData?.procurement_items === 'string') {
                                            try {
                                                items = JSON.parse(rfqData.procurement_items);
                                            } catch {
                                                items = safeUnserialize(rfqData.procurement_items, []);
                                            }
                                        } else if (Array.isArray(rfqData?.procurement_items)) {
                                            items = rfqData.procurement_items;
                                        }

                                        return items.map((item, index) => (
                                            <tr key={index} className="border-b border-gray-200">
                                                <td className="px-4 py-3 border-r border-gray-300">
                                                    <div className="text-sm font-medium text-gray-900">{item}</div>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleCommentBox(index)}
                                                        className={`mt-1 text-xs px-2 py-1 rounded transition-colors ${
                                                            commentVisibility[index] ? 'hidden' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                        }`}
                                                    >
                                                        Add Comment
                                                    </button>
                                                    {commentVisibility[index] && (
                                                        <input
                                                            type="text"
                                                            value={formData.quotation_comments[index] || ''}
                                                            onChange={(e) => handleInputChange('quotation_comments', e.target.value, index)}
                                                            placeholder="Optional comment"
                                                            className="mt-2 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        />
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center border-r border-gray-300">
                                                    <span className="text-sm text-gray-900">{quantities[index] || 0}</span>
                                                </td>
                                                <td className="px-4 py-3 border-r border-gray-300">
                                                    <div className="flex">
                                                        <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">R</span>
                                                        <input
                                                            key={`unit-price-${index}`}
                                                            type="text"
                                                            value={formData.quotation_units?.[index] ?? ''}
                                                            onChange={(e) => handleItemPriceChange(index, e.target.value)}
                                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="0.00"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="text-xs text-red-500 mt-1">
                                                        Debug: idx={index}, val="{formData.quotation_units?.[index]}", len={formData.quotation_units?.length}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center border-r border-gray-300">
                                                    <label className="inline-flex items-center text-xs">
                                                        <input
                                                            id={`vat-checkbox-${index}`}
                                                            type="checkbox"
                                                            checked={vatCheckedStates[index] || false}
                                                            onChange={(e) => toggleVat(index)}
                                                            className="form-checkbox h-4 w-4 text-blue-600"
                                                        />
                                                        <span className="ml-1">Add VAT</span>
                                                    </label>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex">
                                                        <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">R</span>
                                                        <input
                                                            type="text"
                                                            value={formData.quotation_amounts[index] || ''}
                                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md bg-gray-50"
                                                            readOnly
                                                        />
                                                    </div>
                                                    {/* VAT Amount Display - moved here below total input */}
                                                    <div className="vat-display mt-1" id={`vat-display-${index}`}>
                                                        {vatAmounts[index] > 0 && vatCheckedStates[index] && (
                                                            <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                                                VAT: R{vatAmounts[index].toLocaleString('en-US', {
                                                                    maximumFractionDigits: 2,
                                                                    minimumFractionDigits: 2
                                                                }).replace(/,/g, ' ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ));
                                    } catch (error) {
                                        // Error rendering quotation items
                                        return (
                                            <tr>
                                                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                    <div>Unable to load quotation items</div>
                                                    <div className="text-xs mt-2">Please refresh the page or contact support</div>
                                                </td>
                                            </tr>
                                        );
                                    }
                                })()}
                                
                                {/* VAT Row */}
                                <tr className="bg-gray-50">
                                    <td colSpan="3" className="px-4 py-3"></td>
                                    <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">VAT</td>
                                    <td className="px-4 py-3">
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">R</span>
                                            <input
                                                type="text"
                                                value={formData.quotation_vat}
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md bg-gray-100"
                                                readOnly
                                            />
                                        </div>
                                    </td>
                                </tr>
                                
                                {/* Total Row */}
                                <tr className="bg-gray-100">
                                    <td colSpan="3" className="px-4 py-3"></td>
                                    <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">Total</td>
                                    <td className="px-4 py-3">
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md font-bold">R</span>
                                            <input
                                                type="text"
                                                value={formData.quotation_total}
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md bg-gray-100 font-bold"
                                                readOnly
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

 
                {/* Action Buttons */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex space-x-4">
                    <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                    >
                        {submitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                Save Progress
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={async (e) => {
                            const result = await Swal.fire({
                                title: 'Complete Submission?',
                                text: 'Are you sure you want to complete this submission? This action will send your quotation for review.',
                                icon: 'question',
                                showCancelButton: true,
                                confirmButtonColor: '#2563eb',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, submit it',
                                cancelButtonText: 'Cancel'
                            });
                            if (result.isConfirmed) {
                                handleSubmit(e, 'complete');
                            }
                        }}
                        disabled={submitting}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                    >
                        {submitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Submitting...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                Complete Submission
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 mx-auto" style={{ borderColor: 'var(--color-primary)' }}></div>
                    <p className="mt-4 text-lg" style={{ color: 'var(--text-secondary)' }}>Loading RFQ details...</p>
                </div>
            </div>
        );
    }

    if (error && !rfqData) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
                <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 13.5c-.77.833-.23 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>RFQ Not Found</h3>
                        <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center space-x-4">
                            {/* Company Logo */}
                            {settings?.site_logo_preview && (
                                <img 
                                    src={settings.site_logo_preview} 
                                    alt="Company Logo" 
                                    className="h-10 w-auto"
                                />
                            )} 
                        </div>
                        <div className="flex items-center space-x-4">
                            {/* Download Specification Button - Shows for non-completed statuses */}
                            {!isCompleted() && encryptedParams.rfq && (
                                <button
                                    onClick={handleDownloadSpecification}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    title="Download Specification"
                                >
                                    <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Download Specification
                                </button>
                            )}
                            
              
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                {/* RFQ Information Card */}
                <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
                    <div className="px-4 py-4 sm:px-6" style={{ background: 'var(--gradient-primary)' }}>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center space-x-2">
                                    <h3 className="text-lg leading-6 font-medium text-white">
                                        {rfqData?.procurement_ref || 'RFQ Information'}
                                    </h3>
                                    {/* Show EXTENDED badge if procurement has been extended */}
                                    {(rfqData?.procurement_extend_date || rfqData?.procurement_extend_time) && (
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white text-blue-800">
                                            EXTENDED
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 text-sm text-white opacity-90">
                                    {rfqData?.procurement_title || 'N/A'}
                                </p>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-white opacity-90">Submission Deadline</div>
                                <div className="text-white font-semibold">
                                    {rfqData?.procurement_closing && rfqData?.procurement_closing_time ? 
                                        `${rfqData.procurement_closing} ${rfqData.procurement_closing_time}` : 'N/A'}
                                </div>
                                <div className="mt-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        (() => {
                                            if (isCompleted()) return 'bg-green-100 text-green-800';
                                            if (isPendingConfirmation()) return 'bg-yellow-100 text-yellow-800';
                                            
                                            const quotationStatus = parseInt(quoteDetails?.quotation_status) || 0;
                                            if (quotationStatus < 2 && isRFQOpen()) return 'bg-white text-blue-800';
                                            
                                            return 'bg-red-100 text-red-800';
                                        })()
                                    }`}>
                                        {(() => {
                                            if (isCompleted()) return 'Quotation Submitted';
                                            if (isPendingConfirmation()) return 'Pending Confirmation';
                                            
                                            const quotationStatus = parseInt(quoteDetails?.quotation_status) || 0;
                                            if (quotationStatus < 2 && isRFQOpen()) return 'Open for Submissions';
                                            
                                            return 'Closed';
                                        })()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Completion notification - shows if quotation_status == 3 */}
                    {isCompleted() && (
                        <div className="px-4 py-3 bg-blue-50 border-t border-blue-200">
                            <div className="text-blue-800 text-sm">
                                Your submission was completed at: {' '}
                                {quoteDetails?.quotation_time 
                                    ? new Date(quoteDetails.quotation_time * 1000).toLocaleString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour12: false
                                    })
                                    : 'Unknown'
                                }
                            </div>
                        </div>
                    )}
                    
                    {/* Download button - shows if download params are available after confirmation */}
                    
                    {/* Unpublished/Closed notification - shows if RFQ is unpublished or closed */}
                    {!isRFQOpen() && !isCompleted() && (
                        <div className={`px-4 py-3 border-t ${
                            isRFQUnpublished()
                                ? 'bg-red-50 border-red-200'
                                : 'bg-yellow-50 border-yellow-200'
                        }`}>
                            <div className={`text-sm font-medium ${
                                isRFQUnpublished()
                                    ? 'text-red-800'
                                    : 'text-yellow-800'
                            }`}>
                                {isRFQUnpublished()
                                    ? 'This request for quotation has been unpublished and is no longer available'
                                    : 'This request for quotation is now closed'
                                }
                            </div>
                        </div>
                    )}
                </div>

            
                {/* Show appropriate form based on status - matching PHP logic exactly */}
                {(() => {
                    // PHP logic: if quotation_status == 2, show confirmation form
                    if (isPendingConfirmation()) {
                        return (
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                                <div className="px-6 py-4">
                                    <div className="alert alert-info mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
                                        <h6 className="text-blue-800 font-medium">
                                            A confirmation code has been sent to {quoteDetails?.quotation_email_address || quoteDetails?.E_Mail}.
                                        </h6>
                                        <p className="text-blue-700 text-sm mt-2">
                                            Click the button below to enter your confirmation code.
                                        </p>
                                    </div>

                                    <div className="flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() => handleConfirmSubmission()}
                                            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-5 h-5 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Enter Confirmation Code
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    
                    // PHP logic: if quotation_status == 3, show completed view
                    if (isCompleted()) {
                        return (
                            pdfDownloadData && pdfDownloadData.params && (
                                 <div className="bg-white shadow rounded-lg overflow-hidden">
                                    <div className="px-6 py-4">
                                        <div className="text-center">
                                            <button
                                                onClick={handleDownloadPDF}
                                                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center gap-2"
                                            >
                                                <i className="fa fa-cloud-download"></i>
                                                Download Your Quotation Copy
                                            </button>
                                            <p className="text-sm text-gray-600 mt-3">Download your submitted quotation as PDF</p>
                                        </div> 
                                </div>
                            </div>
                            )
                        );
                    }
                    
                    // PHP logic: if quotation_status < 2 AND procurement_closed == 0 AND time() < deadline
                    const quotationStatus = parseInt(quoteDetails?.quotation_status) || 0;
                    if (quotationStatus < 2 && isRFQOpen()) {
                        return (
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                                <form onSubmit={(e) => handleSubmit(e, 'save')}>
                                    {/* Error Message */}
                                    {error && (
                                        <div className="px-6 py-4 border-b border-red-200 bg-red-50">
                                            <div className="flex">
                                                <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-red-800 text-sm">{error}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Success Message */}
                                    {success && (
                                        <div className="px-6 py-4 border-b border-green-200 bg-green-50">
                                            <div className="flex">
                                                <svg className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-green-800 text-sm">{success}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Supplier Details - Compact Table Format */}
                                    <div className="px-6 py-4 border-b border-gray-200">
                                        <h4 className="text-lg font-medium text-gray-900 mb-4">Supplier Details</h4>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full border border-gray-300">
                                                <tbody className="bg-white">
                                                    <tr className="border-b border-gray-200">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50 w-1/4">Company Name</td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="text"
                                                                value={formData.quotation_company_name}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                                                                readOnly
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-b border-gray-200">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">
                                                            <div className="mb-2">Your Logo</div>
                                                          {logoPreview && ( 

                                                                    <img 
                                                                        src={logoPreview} 
                                                                        alt="Company logo" 
                                                                        className="max-h-20 max-w-40 object-contain border rounded bg-white p-2" 
                                                                        onError={(e) => {
                                                                            // Logo failed to load
                                                                            e.target.style.display = 'none';
                                                                        }}
                                                                    /> 
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                        
                                                            <input
                                                                type="file"
                                                                accept="image/jpeg,image/jpg,image/png,image/gif"
                                                                onChange={handleLogoChange}
                                                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                                            />
                                                            <div className="mt-1 text-xs text-gray-500">
                                                                {logoPreview ? 'Upload your logo if its not displayed on the left or you want to replace the current one' : 'Choose a logo image for your company'}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr className="border-b border-gray-200">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">Contact Number</td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="text"
                                                                value={formData.quotation_tel}
                                                                onChange={(e) => handleInputChange('quotation_tel', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-b border-gray-200">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">Physical Address</td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="text"
                                                                value={formData.quotation_physical_address}
                                                                onChange={(e) => handleInputChange('quotation_physical_address', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr className="border-b border-gray-200">
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">Email Address</td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="text"
                                                                value={formData.quotation_email_address}
                                                                onChange={(e) => handleInputChange('quotation_email_address', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                placeholder="email@example.com, email2@example.com"
                                                            />
                                                            <span className="mt-1 text-xs text-blue-600">You may enter up to 3 comma separated email addresses</span>
                                                        </td>
                                                    </tr>
                                                    {rfqData?.procurement_attach == 1 && (
                                                        <tr>
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50">
                                                                Files
                                                                <div className="mt-1 space-x-2">
                                                                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                                                        Allowed: {settings?.site_allowed_ext || 'PDF, DOC, DOCX'}
                                                                    </span>
                                                                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                                                        Max: {settings?.site_upload_limit || '5'}MB
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowFileUpload(!showFileUpload)}
                                                                    className="mb-4 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                                                >
                                                                    <svg className="inline w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                                    </svg>
                                                                    Attach Files
                                                                </button>
                                                                
                                                                {/* MultiFileInput Component - uploads immediately on selection */}
                                                                {showFileUpload && (
                                                                    <div className="mb-4">
                                                                        <MultiFileInput
                                                                            files={attachedFiles}
                                                                            onChange={(newFiles) => {
                                                                                setAttachedFiles(newFiles);
                                                                                // Upload immediately when files are selected
                                                                                if (newFiles.length > 0) {
                                                                                    handleFileUpload(newFiles);
                                                                                }
                                                                            }}
                                                                            multiple={true}
                                                                            className="w-full"
                                                                        />
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Show uploaded attachments from tb_requisition_attachments */}
                                                                {uploadedAttachments.length > 0 && (
                                                                    <div className="mt-4">
                                                                        <h5 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h5>
                                                                        <ul className="space-y-2">
                                                                            {uploadedAttachments.map((attachment) => (
                                                                                <li key={attachment.attachment_id} className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded flex items-center justify-between group hover:bg-gray-100 transition-colors">
                                                                                    <div className="flex items-center flex-1 min-w-0">
                                                                                        <svg className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                                        </svg>
                                                                                        <span className="truncate" title={attachment.file_name}>
                                                                                            {attachment.file_name}
                                                                                        </span>
                                                                                    </div>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => handleFileDelete(attachment.attachment_id)}
                                                                                        disabled={submitting}
                                                                                        className="ml-2 flex-shrink-0 text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        title="Delete file"
                                                                                    >
                                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                    </button>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Show legacy files from database (fallback) */}
                                                                {quoteDetails?.files && quoteDetails.files.length > 0 && (
                                                                    <div className="mt-4">
                                                                        <h5 className="text-sm font-medium text-gray-700 mb-2">
                                                                            Previously Uploaded Files (legacy):
                                                                        </h5>
                                                                        <ul className="space-y-1">
                                                                            {quoteDetails.files.map((file, index) => (
                                                                                <li key={index} className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded flex items-center justify-between">
                                                                                    <span className="flex-1 truncate" title={file.quotation_file_name}>
                                                                                        {file.quotation_file_name}
                                                                                    </span>
                                                                                    <span className="ml-2 text-xs text-gray-500 flex-shrink-0">(legacy)</span>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Quotation Items - Compact Table */}
                                    <div className="px-6 py-4">
                                        <h4 className="text-lg font-medium text-gray-900 mb-4">Quotation</h4>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full border border-gray-300">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-2/5">Item</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-1/12">Qty</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-1/5">Unit Price</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-1/12">VAT</th>
                                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white">
                                                    {(() => {
                                                        try {
                                                            let items = [];
                                                            if (typeof rfqData?.procurement_items === 'string') {
                                                                try {
                                                                    items = JSON.parse(rfqData.procurement_items);
                                                                } catch {
                                                                    items = safeUnserialize(rfqData.procurement_items, []);
                                                                }
                                                            } else if (Array.isArray(rfqData?.procurement_items)) {
                                                                items = rfqData.procurement_items;
                                                            }

                                                            return items.map((item, index) => (
                                                                <tr key={index} className="border-b border-gray-200">
                                                                    <td className="px-4 py-3 border-r border-gray-300">
                                                                        <div className="text-sm font-medium text-gray-900">{item}</div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleCommentBox(index)}
                                                                            className={`mt-1 text-xs px-2 py-1 rounded transition-colors ${
                                                                                commentVisibility[index] ? 'hidden' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                            }`}
                                                                        >
                                                                            Add Comment
                                                                        </button>
                                                                        {commentVisibility[index] && (
                                                                            <input
                                                                                type="text"
                                                                                value={formData.quotation_comments[index] || ''}
                                                                                onChange={(e) => handleInputChange('quotation_comments', e.target.value, index)}
                                                                                placeholder="Optional comment"
                                                                                className="mt-2 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center border-r border-gray-300">
                                                                        <span className="text-sm text-gray-900">{quantities[index] || 0}</span>
                                                                    </td>
                                                                    <td className="px-4 py-3 border-r border-gray-300">
                                                                        <div className="flex">
                                                                            <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">R</span>
                                                                            <input
                                                                                key={`unit-price-${index}`}
                                                                                type="text"
                                                                                value={formData.quotation_units?.[index] ?? ''}
                                                                                onChange={(e) => handleItemPriceChange(index, e.target.value)}
                                                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                                placeholder="0.00"
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center border-r border-gray-300">
                                                                        <label className="inline-flex items-center text-xs">
                                                                            <input
                                                                                id={`vat-checkbox-${index}`}
                                                                                type="checkbox"
                                                                                checked={vatCheckedStates[index] || false}
                                                                                onChange={(e) => toggleVat(index)}
                                                                                className="form-checkbox h-4 w-4 text-blue-600"
                                                                            />
                                                                            <span className="ml-1">Add VAT</span>
                                                                        </label>
                                                                    </td>
                                                                    <td className="px-4 py-3">
                                                                        <div className="flex">
                                                                            <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">R</span>
                                                                            <input
                                                                                type="text"
                                                                                value={formData.quotation_amounts[index] || ''}
                                                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md bg-gray-50"
                                                                                readOnly
                                                                            />
                                                                        </div>
                                                                        {/* VAT Amount Display - moved here below total input */}
                                                                        <div className="vat-display mt-1" id={`vat-display-${index}`}>
                                                                            {vatAmounts[index] > 0 && vatCheckedStates[index] && (
                                                                                <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                                                                    VAT: R{vatAmounts[index].toLocaleString('en-US', {
                                                                                        maximumFractionDigits: 2,
                                                                                        minimumFractionDigits: 2
                                                                                    }).replace(/,/g, ' ')}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ));
                                                        } catch (error) {
                                                            // Error rendering quotation items
                                                            return (
                                                                <tr>
                                                                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                                        <div>Unable to load quotation items</div>
                                                                        <div className="text-xs mt-2">Please refresh the page or contact support</div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    })()}
                                                    
                                                    {/* VAT Row */}
                                                    <tr className="bg-gray-50">
                                                        <td colSpan="3" className="px-4 py-3"></td>
                                                        <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">VAT</td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex">
                                                                <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md">R</span>
                                                                <input
                                                                    type="text"
                                                                    value={formData.quotation_vat}
                                                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md bg-gray-100"
                                                                    readOnly
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    
                                                    {/* Total Row */}
                                                    <tr className="bg-gray-100">
                                                        <td colSpan="3" className="px-4 py-3"></td>
                                                        <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">Total</td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex">
                                                                <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md font-bold">R</span>
                                                                <input
                                                                    type="text"
                                                                    value={formData.quotation_total}
                                                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r-md bg-gray-100 font-bold"
                                                                    readOnly
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>


                                    {/* Action Buttons */}
                                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex space-x-4">
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                                        >
                                            {submitting ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                                                    </svg>
                                                    Save Progress
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={async (e) => {
                                                const result = await Swal.fire({
                                                    title: 'Complete Submission?',
                                                    text: 'Are you sure you want to complete this submission? This action will send your quotation for review.',
                                                    icon: 'question',
                                                    showCancelButton: true,
                                                    confirmButtonColor: '#2563eb',
                                                    cancelButtonColor: '#6b7280',
                                                    confirmButtonText: 'Yes, submit it',
                                                    cancelButtonText: 'Cancel'
                                                });
                                                if (result.isConfirmed) {
                                                    handleSubmit(e, 'complete');
                                                }
                                            }}
                                            disabled={submitting}
                                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                                        >
                                            {submitting ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                    </svg>
                                                    Complete Submission
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        );
                    }
                    
                    // Otherwise show closed view
                    return (
                        <div className="bg-white shadow rounded-lg overflow-hidden">
                            <div className="px-6 py-8 text-center">
                                <svg className="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {isRFQUnpublished() ? 'RFQ Unpublished' : 'RFQ Closed'}
                                </h3>
                                <p className="text-sm text-gray-600 mb-4">
                                    {isRFQUnpublished() 
                                        ? 'This request for quotation has been unpublished and is no longer available.'
                                        : 'This request for quotation is no longer accepting submissions.'
                                    }
                                </p>
                                {/* Only show closing date if RFQ is closed (not unpublished) */}
                                {!isRFQUnpublished() && rfqData?.procurement_closing && rfqData?.procurement_closing_time && (
                                    <p className="text-xs text-gray-500">
                                        Closed on: {rfqData.procurement_closing} at {rfqData.procurement_closing_time}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Specification Section - Always visible, matches PHP structure */}
                {rfqData && (
                    <div className="mt-6 bg-white shadow rounded-lg overflow-hidden"> 
                        <div ref={specificationRef} className="specification-pdf-content">
                            {/* PDF Header - Hidden in UI, visible in PDF */}
                            <div className="pdf-header" style={{ padding: '20px', borderBottom: '3px solid #3498db', marginBottom: '20px', display: 'none' }}>
                                <h1 style={{ fontSize: '24px', color: '#2c3e50', margin: '0 0 10px 0', fontWeight: '600' }}>
                                    {settings?.site_name || 'Silulumanzi Water'}
                                </h1>
                                <div style={{ color: '#6c757d', fontSize: '14px' }}>Request for Quotation - Specification</div>
                                <div style={{ marginTop: '15px', fontSize: '12px' }}>
                                    <div><strong>Reference:</strong> {rfqData.procurement_ref}</div>
                                    <div><strong>Title:</strong> {rfqData.procurement_title}</div>
                                    {rfqData.procurement_description && (
                                        <div><strong>Description:</strong> {rfqData.procurement_description}</div>
                                    )}
                                </div>
                            </div>

                                                                {/* Procurement Text */}
                                    {rfqData?.procurement_text && (
                                        <div className="px-6 py-4 border-t border-gray-200">
                                            <div className="prose max-w-none text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: rfqData.procurement_text }} />
                                        </div>
                                    )}

                            
                            <div className="overflow-x-auto">
                                <table className="min-w-full border border-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 w-5/6">Item</th>
                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                    {(() => {
                                        try {
                                            let items = [];
                                            if (typeof rfqData?.procurement_items === 'string') {
                                                try {
                                                    items = JSON.parse(rfqData.procurement_items);
                                                } catch {
                                                    items = safeUnserialize(rfqData.procurement_items, []);
                                                }
                                            } else if (Array.isArray(rfqData?.procurement_items)) {
                                                items = rfqData.procurement_items;
                                            }

                                            if (!items || items.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                                                            No items found in this RFQ
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return items.map((item, index) => (
                                                <tr key={index} className="border-b border-gray-200">
                                                    <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">
                                                        {item}
                                                    </td>
                                                    <td className="px-4 py-3 text-center text-sm text-gray-900">
                                                        {quantities[index] || 0}
                                                    </td>
                                                </tr>
                                            ));
                                        } catch (error) {
                                            // Error rendering specification items
                                            return (
                                                <tr>
                                                    <td colSpan="2" className="px-6 py-8 text-center text-gray-500">
                                                        Unable to load specification items
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    })()}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Specification Attachments - Documents & Files */}
                        {specificationAttachments && specificationAttachments.length > 0 && (
                            <div className="px-6 py-4 border-t border-gray-200">
                                <h4 className="text-lg font-medium text-gray-900 mb-4">Attachments</h4>
                                <div className="space-y-2">
                                    {specificationAttachments.map((attachment, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                                            <div className="flex items-center min-w-0 flex-1">
                                                <svg className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span 
                                                    className="text-sm text-gray-900 break-words"
                                                    style={{ 
                                                        wordBreak: 'break-word',
                                                        lineHeight: '1.4',
                                                        maxWidth: '100%'
                                                    }}
                                                    title={attachment.file_name || attachment.name || `File ${index + 1}`}
                                                >
                                                    {attachment.file_name || attachment.name || `File ${index + 1}`}
                                                </span>
                                            </div>
                                            <div className="flex gap-2 ml-4 flex-shrink-0">
                                                <button
                                                    onClick={() => {
                                                        // View file in new tab with cache-busting
                                                        // Standardized pattern: /api/attachments/{module}/{filename}?v={timestamp}
                                                        const viewUrl = attachment.attachment_id 
                                                            ? `${API_BASE_URL}/api/attachments/specifications/${attachment.file_path}`
                                                            : `${API_BASE_URL}/${attachment.file_path || attachment.url}`;
                                                        window.open(viewUrl + '?v=' + Date.now(), '_blank');
                                                    }}
                                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                                                    title="View file"
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        // Force download with standardized pattern
                                                        // Standardized pattern: /api/attachments/{module}/{filename}?download=1
                                                        const downloadUrl = attachment.attachment_id 
                                                            ? `${API_BASE_URL}/api/attachments/specifications/${attachment.file_path}?download=1`
                                                            : `${API_BASE_URL}/files/storage?file=${encodeURIComponent(attachment.file_path || attachment.url)}`;
                                                        window.open(downloadUrl, '_blank');
                                                    }}
                                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors"
                                                    title="Download file"
                                                >
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    Download
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Quote Terms - matches PHP structure */}
                        {settings?.site_quote_terms && (
                            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                                <div 
                                    className="prose prose-sm max-w-none text-sm text-gray-700"
                                    dangerouslySetInnerHTML={{ __html: settings.site_quote_terms }}
                                />
                            </div>
                        )}
                        </div>
                    </div>
                )}
            </div>
 
        </div>
    );
};
 
export default SupplierQuotation;
